import { Inject, Injectable, InjectionToken, PLATFORM_ID } from '@angular/core';
import type { FactoryProvider } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

/* removed local storage. using session storage instead as none of the data needs to persist*/

export const SESSION_STORAGE = new InjectionToken('sessionStorageToken');

@Injectable({
    providedIn: 'root'
})
export class SessionStorage {
    private data: { [key: string]: string } = {};

    constructor(
        @Inject(PLATFORM_ID) private platformId: Object
    ) { }

    public setItem(key: string, val: string): void {
        if (this.canUseSessionStorage()) {
            try {
                sessionStorage.setItem(key, val);
                return;
            } catch {
                // Fall back to memory storage if sessionStorage is blocked.
            }
        }

        this.data[key] = val;
    }

    public getItem(key: string): string {
        if (this.canUseSessionStorage()) {
            try {
                return sessionStorage.getItem(key);
            } catch {
                // Fall back to memory storage if sessionStorage is blocked.
            }
        }

        return this.data[key] ?? null;
    }

    public removeItem(key: string): void {
        if (this.canUseSessionStorage()) {
            try {
                sessionStorage.removeItem(key);
                return;
            } catch {
                // Fall back to memory storage if sessionStorage is blocked.
            }
        }

        delete this.data[key];
    }

    public clear(): void {
        if (this.canUseSessionStorage()) {
            try {
                sessionStorage.clear();
                return;
            } catch {
                // Fall back to memory storage if sessionStorage is blocked.
            }
        }

        this.data = {};
    }

    public get length(): number {
        if (this.canUseSessionStorage()) {
            try {
                return sessionStorage.length;
            } catch {
                // Fall back to memory storage if sessionStorage is blocked.
            }
        }

        return Object.keys(this.data).length;
    }

    public key(index: number): string|null {
        if (this.canUseSessionStorage()) {
            try {
                return sessionStorage.key(index);
            } catch {
                // Fall back to memory storage if sessionStorage is blocked.
            }
        }

        const keys = Object.keys(this.data);
        if (index >= keys.length) return null;
        return keys[index];
    }

    private canUseSessionStorage(): boolean {
        return isPlatformBrowser(this.platformId) && typeof sessionStorage !== 'undefined';
    }
}

export function sessionStorageFactory(platformId: Object): SessionStorage {
    return new SessionStorage(platformId);
}

export const sessionStorageProvider: FactoryProvider = {
    provide: SESSION_STORAGE,
    useFactory: sessionStorageFactory,
    deps: [PLATFORM_ID]
};

export const SESSION_STORAGE_PROVIDERS = [
    sessionStorageProvider
];
